import React from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { keyBy } from 'lodash';
import {
  withStyles,
  withMobileDialog,
  IconButton,
  Icon,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  DialogContentText,
  Button,
} from '@material-ui/core';
import {
  deleteDashboard as deleteDashboardAction,
  setSelectedDashboard as setSelectedDashboardAction,
  editDashboard as editDashboardAction,
  getDashboards as getDashboardsAction,
  toggleDashboardFilter as toggleDashboardFilterAction,
} from '@pingum/app-state-management/dashboard/actions';
import { getEventsExport as exportEventsAction } from '@pingum/app-state-management/event/actions';
import {
  getEventTemplates as getEventTemplatesAction,
  selectEventTemplate,
} from '@pingum/app-state-management/eventTemplate/actions';
import {
  getEventSubscription as getEventSubscriptionAction,
  updateEventSubscription as updateEventSubscriptionAction,
} from '@pingum/app-state-management/eventSubscription/actions';
import { getStatuses as getStatusesAction } from '@pingum/app-state-management/status/actions';
import { getEventData as getEventDataAction } from '@pingum/app-state-management/eventData/actions';
import { getTags as getTagsAction } from '@pingum/app-state-management/tag/actions';
import { getTagTypes as getTagTypesAction } from '@pingum/app-state-management/tagType/actions';
import {
  getAllQueryParams,
  getQueryParam,
  getTenantFromUrl,
} from '../../../../utils/urlHelper';
import { getEventTemplateDashboard } from '../../../../utils/dashboardHelper';
import DashboardSelector from '../DashboardSelector';
import DashboardViewSelector from '../DashboardViewSelector';
import DashboardActions from '../DashboardActions';
import CopyDashboardDialog from '../dialogs/CopyDashboardDialog';
import EditDashboardDialog from '../dialogs/EditDashboardDialog';
import DeleteDashboardDialog from '../dialogs/DeleteDashboardDialog';
import CreateEventButton from '../../../widgets/CreateEventButton';
import styles from './styles';

const isDefaultDashboard = (dashboard) => dashboard.id <= 0;
const getDashboardDirectLink = (history) => history.location.pathname.replace(/^\/dashboards\/?/, '');
const isEventTemplateDashboard = (dashboardDirectLink) => dashboardDirectLink.indexOf('workflows/') === 0;

export class DashboardHeaderComponent extends React.Component {
  constructor(props) {
    super(props);
    const {
      getEventSubscription: getEventSubscriptionProp,
      selectedDashboard,
      history,
      userId,
    } = this.props;

    this.state = {
      openConfirmDeleteDashboardDialog: false,
      openConfirmCopyDashboardDialog: false,
      openConfirmEditDashboardDialog: false,
      openExportAlertModal: false,
    };

    getEventSubscriptionProp(userId);

    /*
      we only want to set the dashboard if one was previously selected.
      This MUST be done before loadData() is called as the dashboard in the
      URL triggeres what is set for selected dashboard
    */
    if (selectedDashboard.id && selectedDashboard.id >= 0) {
      history.push({ pathname: '/dashboards/'.concat(selectedDashboard.directLinkName) });
    } else if (
      global.window.localStorage.getItem(`${getTenantFromUrl()}_last_dashboard_selection`)
        && (window.location.pathname === '/dashboards' || window.location.pathname === '/dashboards/')
    ) {
      // we have a saved last selection of a dashboard so let's load that up
      history.push({ pathname: '/dashboards/'.concat(global.window.localStorage.getItem(`${getTenantFromUrl()}_last_dashboard_selection`)) });
    }

    this.loadData();
  }

  componentDidUpdate(oldProps) {
    const {
      selectedDashboard,
      eventTemplates,
      history,
      setSelectedDashboard,
    } = this.props;
    const dashboardDirectLink = getDashboardDirectLink(history);
    if (isEventTemplateDashboard(dashboardDirectLink) && oldProps.eventTemplates.length !== eventTemplates.length) {
      const selectedEventTemplate = keyBy(eventTemplates, 'id')[parseInt(dashboardDirectLink.replace('workflows/', ''), 10)];
      const newSelectedDashboard = getEventTemplateDashboard(selectedEventTemplate);
      setSelectedDashboard(newSelectedDashboard);
    }
    if (
      oldProps.selectedDashboard.id !== selectedDashboard.id
        && !(isDefaultDashboard(selectedDashboard) && isEventTemplateDashboard(dashboardDirectLink))
    ) {
      this.applyFilter(selectedDashboard.dashboardData);
      this.handleSelectedDashboardChange(selectedDashboard);
    }
  }

  applyFilter = (dashboard) => {
    const {
      toggleDashboardFilter,
      applyFilterToSearch,
    } = this.props;
    const { sortStartDateDesc } = this.state;
    toggleDashboardFilter(false);
    applyFilterToSearch(
      dashboard,
      sortStartDateDesc,
      true,
    );
  }

  handleSelectedDashboardChange = (dashboard) => {
    const {
      setSelectedDashboard,
      setSelectedEventTemplate,
      history,
    } = this.props;
    setSelectedDashboard(dashboard);
    history.push({
      pathname: `/dashboards/${dashboard.directLinkName}`,
      search: getAllQueryParams(),
    });
    global.window.localStorage.setItem(`${getTenantFromUrl()}_last_dashboard_selection`, dashboard.directLinkName);

    setSelectedEventTemplate(dashboard.eventTemplate || {});
  }

  userHasAccessToDashboard = () => {
    const {
      setSelectedDashboard: setSelectedDashboardProp,
      admin,
    } = this.props;
    if (isDefaultDashboard(setSelectedDashboardProp)) {
      // can't do anything with default dashboard
      return false;
    }

    if (!setSelectedDashboardProp.organizationExposed) {
      // personal dashboard
      return true;
    }

    if (admin) {
      // user is an admin
      return true;
    }

    // no access
    return false;
  }

  handleFilterClick = () => {
    const { toggleDashboardFilter } = this.props;
    toggleDashboardFilter(true);
  }

  handleOpenDelete = () => {
    this.setState({ openConfirmDeleteDashboardDialog: true });
  }

  handleCloseDelete = () => {
    this.setState({ openConfirmDeleteDashboardDialog: false });
  }

  handleDeleteConfirm = () => {
    const {
      selectedDashboard,
      deleteDashboard: deleteDashboardProp,
      toggleDashboardFilter,
      history,
    } = this.props;
    deleteDashboardProp(selectedDashboard.id);
    this.setState({ openConfirmDeleteDashboardDialog: false });
    toggleDashboardFilter(false);
    history.push({ pathname: '/dashboards' });
  }

  canSaveDashboard = (pendingChanges) => pendingChanges
    && this.userHasAccessToDashboard()

  handleSave = () => {
    const {
      editDashboard: editDashboardProp,
      selectedDashboard,
    } = this.props;

    delete selectedDashboard.dashboardData.tagNames;
    delete selectedDashboard.dashboardData.statusNames;
    delete selectedDashboard.dashboardData.eventTemplateId;
    delete selectedDashboard.dashboardData.pastDue;

    editDashboardProp(
      selectedDashboard.id,
      {
        dashboardData: selectedDashboard.dashboardData,
        defaultView: getQueryParam('view'),
      },
    );
  }

  handleExport = () => {
    const {
      totalEvents,
      currentFilterDashboard,
      selectedView,
      exportEvents,
    } = this.props;
    if (totalEvents > 1000) {
      this.setState({ openExportAlertModal: true });
    } else {
      const request = currentFilterDashboard.dashboardData;
      if (selectedView.key === 'analytics') {
        // if in analytics and without complete or start date set, set here now
        request.includeComplete = true;
        if (!request.startDate) {
          // show last 7 days
          const sevenDaysAgo = new Date();
          sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
          request.startDate = sevenDaysAgo;
        }
      }
      exportEvents(request);
    }
  }

  handleCloseExportAlertModal = () => {
    this.setState({ openExportAlertModal: false });
  }

  handleOpenCopy = () => {
    this.setState({ openConfirmCopyDashboardDialog: true });
  }

  handleCloseCopy = () => {
    this.setState({ openConfirmCopyDashboardDialog: false });
  }

  handleCopyConfirm = (newDashboard) => {
    const { toggleDashboardFilter } = this.props;
    this.setState({ openConfirmCopyDashboardDialog: false });
    toggleDashboardFilter(false);
    this.handleSelectedDashboardChange(newDashboard);
  }

  handleOpenEdit = () => {
    this.setState({ openConfirmEditDashboardDialog: true });
  }

  handleCloseEdit = () => {
    this.setState({ openConfirmEditDashboardDialog: false });
  }

  handleEditConfirm = (changedDashboard) => {
    const {
      toggleDashboardFilter,
      selectedDashboard,
    } = this.props;

    delete selectedDashboard.dashboardData.tagNames;
    delete selectedDashboard.dashboardData.statusNames;

    this.handleSelectedDashboardChange({
      ...selectedDashboard,
      ...changedDashboard,
    });
    this.setState({ openConfirmEditDashboardDialog: false });
    toggleDashboardFilter(false);
  }

  handleSubscriptionChange = (event) => {
    const {
      updateEventSubscription: updateEventSubscriptionProp,
      userId,
      selectedDashboard,
    } = this.props;
    if (event.target.value === 'true') {
      // we were subscribed so now we need to move it
      updateEventSubscriptionProp(
        userId,
        { dashboards: { remove: [selectedDashboard.id] } },
      );
    } else {
      // we are not subscribed so add it
      updateEventSubscriptionProp(
        userId,
        { dashboards: { add: [selectedDashboard.id] } },
      );
    }
  }

  handleOrganizationExposedChange = (event) => {
    const {
      editDashboard: editDashboardProp,
      selectedDashboard,
    } = this.props;
    if (event.target.value === 'true') {
      // dashboard was exposed so we will make private
      editDashboardProp(
        selectedDashboard.id,
        { organizationExposed: false },
      );
    } else {
      // dashboard not exposed so we will expose it
      editDashboardProp(
        selectedDashboard.id,
        { organizationExposed: true },
      );
    }
  }

  loadData = () => {
    const {
      getDashboards,
      getStatuses,
      getTagTypes,
      getTags,
      getEventData,
      selectedDashboard,
      history,
      statuses,
      tagTypes,
      tags,
      eventData,
      eventTemplates,
      getEventTemplates,
    } = this.props;
    const dashboardDirectLink = getDashboardDirectLink(history);

    if (eventTemplates.length === 0) {
      getEventTemplates();
    }

    if (!selectedDashboard.id) {
      // only load dashboard if we don't already have one set
      getDashboards(dashboardDirectLink, getQueryParam('view'));
    }

    if (statuses.length === 0) {
      getStatuses();
    }
    if (tagTypes.length === 0) {
      getTagTypes();
    }
    if (tags.length === 0) {
      getTags();
    }
    if (eventData.length === 0) {
      getEventData();
    }
  }

  render() {
    const {
      dashboards,
      selectedDashboard,
      eventSubscription,
      selectedView,
      classes,
      pendingChanges,
      restricted,
      eventTemplates,
    } = this.props;
    const {
      openConfirmDeleteDashboardDialog,
      openConfirmCopyDashboardDialog,
      openConfirmEditDashboardDialog,
      openExportAlertModal,
    } = this.state;

    return (
      <div className={classes.container}>
        <div className={classes.selector}>
          <DashboardSelector
            dashboards={dashboards}
            selectedDashboard={selectedDashboard}
            onDashboardChange={this.handleSelectedDashboardChange}
            eventTemplates={eventTemplates}
          />
        </div>

        {
          pendingChanges
            && (
              <div className={classes.pendingChanges}>
                <Tooltip title="Changes not saved.">
                  <IconButton>
                    <Icon className={classes.icon}>
                      warning
                    </Icon>
                  </IconButton>
                </Tooltip>
              </div>
            )
        }

        {
          !restricted
            && <CreateEventButton />
        }

        {
          <Tooltip title="Search">
            <IconButton
              onClick={() => this.props.toggleDashboardFilter(true)}
            >
              <Icon className={classes.icon}>search</Icon>
            </IconButton>
          </Tooltip>
        }

        {
          <Tooltip title="Refresh">
            <IconButton
              onClick={() => this.props.applyFilterToSearch(
                this.props.selectedDashboard.dashboardData,
                null,
                true,
              )}
            >
              <Icon className={classes.icon}>cached</Icon>
            </IconButton>
          </Tooltip>
        }

        <DashboardViewSelector />

        <DashboardActions
          selectedDashboard={selectedDashboard}
          onCopy={this.handleOpenCopy}
          onEdit={this.handleOpenEdit}
          onDelete={this.handleOpenDelete}
          onExport={this.handleExport}
          onSave={this.handleSave}
          saveEnabled={this.canSaveDashboard(pendingChanges)}
          isAdmin={this.userHasAccessToDashboard()}
          subscribeEnabled={!isDefaultDashboard(selectedDashboard)}
          onSubscriptionChange={this.handleSubscriptionChange}
          isSubscribedToDashboard={
            eventSubscription.dashboards
              .indexOf(selectedDashboard.id) >= 0
          }
          onOrganizationExposedChange={this.handleOrganizationExposedChange}
          handleFilterClick={this.handleFilterClick}
        />

        <DeleteDashboardDialog
          open={openConfirmDeleteDashboardDialog}
          onClose={this.handleCloseDelete}
          onConfirm={this.handleDeleteConfirm}
          onCancel={this.handleCloseDelete}
        />

        <CopyDashboardDialog
          open={openConfirmCopyDashboardDialog}
          currentView={selectedView.name}
          handleConfirm={this.handleCopyConfirm}
          handleClose={this.handleCloseCopy}
          userIsAdmin={this.userHasAccessToDashboard()}
          currentFilter={selectedDashboard.dashboardData}
        />

        <EditDashboardDialog
          open={openConfirmEditDashboardDialog}
          currentView={selectedView.name}
          handleConfirm={this.handleEditConfirm}
          handleClose={this.handleCloseEdit}
        />

        <Dialog
          open={openExportAlertModal}
          onClose={this.handleCloseExportAlertModal}
          aria-labelledby="export-alert-dialog-title"
          aria-describedby="export-alert-dialog-description"
        >
          <DialogTitle id="export-alert-dialog-title">Unable to Export Events</DialogTitle>
          <DialogContent>
            <DialogContentText id="export-alert-dialog-description">
              Cannot export more than 1000 events, which the current dashboard filter encompasses.
              Apply additional filters or restrict the date range to reduce the number of
              records returned.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={this.handleCloseExportAlertModal} color="primary" autoFocus>
              OK
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    );
  }
}

DashboardHeaderComponent.propTypes = {
  dashboards: PropTypes.array,
  selectedDashboard: PropTypes.object,
  setSelectedDashboard: PropTypes.func,
  classes: PropTypes.object,
  deleteDashboard: PropTypes.func,
  getEventSubscription: PropTypes.func,
  updateEventSubscription: PropTypes.func,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
    search: PropTypes.string,
    location: PropTypes.shape({ pathname: PropTypes.string }),
  }).isRequired,
  pendingChanges: PropTypes.bool,
  eventSubscription: PropTypes.object,
  editDashboard: PropTypes.func,
  applyFilterToSearch: PropTypes.func,
  getDashboards: PropTypes.func,
  getStatuses: PropTypes.func,
  getEventData: PropTypes.func,
  getTags: PropTypes.func,
  getTagTypes: PropTypes.func,
  toggleDashboardFilter: PropTypes.func,
  totalEvents: PropTypes.number,
  currentFilterDashboard: PropTypes.object,
  exportEvents: PropTypes.func,
  selectedView: PropTypes.object,
  statuses: PropTypes.array,
  tags: PropTypes.array,
  tagTypes: PropTypes.array,
  eventData: PropTypes.array,
  userId: PropTypes.string,
  admin: PropTypes.bool,
  restricted: PropTypes.bool,
  eventTemplates: PropTypes.array,
  getEventTemplates: PropTypes.func,
  setSelectedEventTemplate: PropTypes.func,
};

DashboardHeaderComponent.defaultProps = {
  dashboards: [],
  selectedDashboard: {},
  setSelectedDashboard: () => {},
  classes: {},
  deleteDashboard: () => {},
  getEventSubscription: () => {},
  updateEventSubscription: () => {},
  pendingChanges: false,
  eventSubscription: {
    dashboards: [],
    eventTemplates: [],
  },
  editDashboard: () => {},
  applyFilterToSearch: () => {},
  getDashboards: () => {},
  getStatuses: () => {},
  getEventData: () => {},
  getTags: () => {},
  getTagTypes: () => {},
  toggleDashboardFilter: () => {},
  totalEvents: 0,
  currentFilterDashboard: {},
  exportEvents: () => {},
  selectedView: {},
  statuses: [],
  tags: [],
  tagTypes: [],
  eventData: [],
  userId: '',
  admin: false,
  restricted: false,
  eventTemplates: [],
  getEventTemplates: () => {},
  setSelectedEventTemplate: () => {},
};

const mapStateToProps = (state) => ({
  dashboards: state.dashboard.dashboards,
  selectedDashboard: state.dashboard.currentFilterDashboard,
  eventSubscription: state.eventSubscription.eventSubscription,
  pendingChanges: state.dashboard.pendingChanges,
  currentFilterDashboard: { ...state.dashboard.currentFilterDashboard },
  selectedView: state.dashboard.selectedView,
  statuses: state.status.statuses,
  tags: state.tag.tags,
  tagTypes: state.tagType.tagTypes,
  eventData: state.eventData.eventData,
  eventTemplates: state.eventTemplate.eventTemplatesPageable.content,
});

const mapDispatchToProps = (dispatch) => ({
  setSelectedDashboard: (dashboard) => dispatch(setSelectedDashboardAction(dashboard)),
  deleteDashboard: (id) => dispatch(deleteDashboardAction(id)),
  getEventSubscription: (auth0UserId) => dispatch(getEventSubscriptionAction(auth0UserId)),
  updateEventSubscription: (auth0UserId, request) => dispatch(updateEventSubscriptionAction(auth0UserId, request)),
  editDashboard: (id, request) => dispatch(editDashboardAction(id, request)),
  getDashboards: (dashboardDirectLink, queryParamView) => dispatch(getDashboardsAction(dashboardDirectLink, queryParamView)),
  getStatuses: () => dispatch(getStatusesAction(false)),
  getEventData: () => dispatch(getEventDataAction(false)),
  getTags: () => dispatch(getTagsAction(false, false)),
  getTagTypes: () => dispatch(getTagTypesAction(false, false)),
  toggleDashboardFilter: (state) => dispatch(toggleDashboardFilterAction(state)),
  exportEvents: (filter) => dispatch(exportEventsAction(filter)),
  getEventTemplates: () => dispatch(getEventTemplatesAction(false, {
    page: 0,
    size: 10,
    search: '',
    sort: ['name,asc'],
  })),
  setSelectedEventTemplate: (eventTemplate) => dispatch(selectEventTemplate(eventTemplate)),
});

export const DashboardHeaderConnected = connect(
  mapStateToProps,
  mapDispatchToProps,
)(withMobileDialog({ breakpoint: 'xs' })(withStyles(styles)(DashboardHeaderComponent)));
export default withRouter(DashboardHeaderConnected);
